import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  icon: React.ReactNode;
  title: string;
  breadcrumb: BreadcrumbItem[];
}

const initials = (firstName?: string, lastName?: string) => {
  const f = firstName?.[0] ?? "";
  const l = lastName?.[0] ?? "";
  return (f + l).toUpperCase() || "?";
};

// Single shared header for every logged-in page — student, mentor, and admin alike.
// Only icon/title/breadcrumb differ per page; identity chip + logout are identical
// everywhere so logout is always present and never styled differently page to page.
const PageHeader: React.FC<PageHeaderProps> = ({ icon, title, breadcrumb }) => {
  const { student, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="bg-gradient-to-r from-[#009fe3] to-[#0077b3]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-[38px] h-[38px] rounded-xl bg-white/18 flex items-center justify-center flex-shrink-0 text-white text-lg">
            {icon}
          </div>
          <div>
            <h1 className="text-[17px] font-extrabold tracking-tight leading-tight">
              <span className="text-black">G</span>
              <span className="text-yellow-400">2</span>
              <span className="text-red-600">G</span>
              <span className="text-white/50 mx-1.5">|</span>
              <span className="text-white">{title}</span>
            </h1>
            <nav className="flex items-center gap-1.5 text-[11.5px] text-white/75 mt-0.5">
              {breadcrumb.map((item, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span>/</span>}
                  {item.to ? (
                    <Link to={item.to} className="hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  ) : (
                    <span className="text-white font-semibold">{item.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/15">
            <span className="w-[26px] h-[26px] rounded-full bg-white text-[#0077b3] text-[10.5px] font-bold flex items-center justify-center flex-shrink-0">
              {initials(student?.firstName, student?.lastName)}
            </span>
            <span className="text-xs font-semibold text-white">
              {student?.firstName
                ? `${student.firstName} ${student.lastName ?? ""}`.trim()
                : "User"}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-white/15 text-white hover:bg-white/28 transition-colors"
          >
            ⏻ Logout
          </button>
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
